
import React, { useState, useEffect, useRef } from 'react';
import { toDisplayDate } from '@/lib/orgFormat';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Users, UserPlus, Eye, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { isValidPhone } from '@/features/shared/validation/phone';
import { PhoneInput } from '@/features/shared/components/PhoneInput';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { useHasPermission } from '@/rbac/useRbac';
import { EmployeeSearchFilters } from './EmployeeSearchFilters';
import { ViewModeToggle } from './ViewModeToggle';
import { EmployeeCard } from './EmployeeCard';
import { EmployeeListItem } from './EmployeeListItem';
import { getAllEmployees, getEmployees, getDepartments, getJobTitles, getTeamOut, getReadinessSummary, createEmployee, type EmployeeCreateInput, Employee as ApiEmployee } from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';
import { DANA, danaMatchesSearch } from '@/lib/dana';

interface Employee {
  id: string;
  userId?: string;
  name: string;
  position: string;
  department: string;
  email: string;
  phone: string;
  status: 'active' | 'on_leave' | 'inactive';
  avatar?: string;
  hireDate: string;
  managerId?: string;
  isAssistant?: boolean;
  isDraft?: boolean;
  incompleteCount?: number;
  contractExpired?: boolean; // end date passed while still Active (HR follow-up needed)
}

// Dana rendered as a virtual directory entry (pinned, not counted as an employee).
const DANA_ENTRY: Employee = {
  id: DANA.id,
  name: DANA.name,
  position: DANA.position,
  department: DANA.department,
  email: DANA.email,
  phone: '',
  status: 'active',
  hireDate: '',
  isAssistant: true,
};

// Map the backend status values onto the UI's status union.
const mapStatus = (s?: string): Employee['status'] => {
  switch ((s || '').toLowerCase()) {
    case 'on_leave': return 'on_leave';
    case 'inactive':
    case 'terminated':
    case 'suspended': return 'inactive';
    default: return 'active';
  }
};

export const EmployeeDirectory: React.FC = () => {
  const { t } = useTranslation(['employees']);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Seed department/status filters from the URL (e.g. arriving from the
  // Departments screen's "N employees" headcount → ?department=Sales&status=active).
  const [departmentFilter, setDepartmentFilter] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('department') || 'all'; }
    catch { return 'all'; }
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('status') || 'all'; }
    catch { return 'all'; }
  });
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<string[]>([]);
  const [titleOptions, setTitleOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);   // initial load
  const [searching, setSearching] = useState(false); // background refetch
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0); // bump to refetch (retry)
  const loadedOnce = useRef(false);

  const { isManager, isTenantAdmin, isEmployee } = useRoleAccess();

  const canManageEmployees = isManager || isTenantAdmin;
  // Canonical RBAC gates for write actions.
  const canWrite = useHasPermission('employee:write');
  const canImport = useHasPermission('employee:import');

  // Add-employee inline form.
  const emptyForm: EmployeeCreateInput = {
    employee_id: '', first_name: '', middle_name: '', last_name: '', preferred_name: '',
    email: '', position: '', hire_date: '', phone: '',
  };
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<EmployeeCreateInput>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof EmployeeCreateInput, string>>>({});
  // Lifecycle L2: create as Draft so the onboarding checklist auto-starts and
  // the person can't be paid until it completes. Default ON (the controlled path).
  const [startOnboarding, setStartOnboarding] = useState(true);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const req = (v?: string) => t('employees:directory.field_required', { defaultValue: 'This field is required.' });
  const validate = (f: EmployeeCreateInput): Partial<Record<keyof EmployeeCreateInput, string>> => {
    const e: Partial<Record<keyof EmployeeCreateInput, string>> = {};
    if (!f.first_name?.trim()) e.first_name = req();
    if (!f.last_name?.trim()) e.last_name = req();
    if (!f.employee_id?.trim()) e.employee_id = req();
    if (!f.position?.trim()) e.position = req();
    if (!f.email?.trim()) e.email = req();
    else if (!EMAIL_RE.test(f.email.trim())) e.email = t('employees:directory.invalid_email', { defaultValue: 'Enter a valid email address.' });
    if (!f.hire_date?.trim()) e.hire_date = req();
    else {
      const d = toDisplayDate(f.hire_date);
      const maxFuture = new Date(); maxFuture.setFullYear(maxFuture.getFullYear() + 1);
      if (isNaN(d.getTime()) || d.getFullYear() < 1950) e.hire_date = t('employees:directory.invalid_date', { defaultValue: 'Enter a valid hire date.' });
      else if (d > maxFuture) e.hire_date = t('employees:directory.date_too_future', { defaultValue: 'Hire date is too far in the future.' });
    }
    if (f.phone?.trim() && !isValidPhone(f.phone)) e.phone = t('employees:directory.invalid_phone', { defaultValue: 'Enter a valid phone number, e.g. 0712345678 or +255712345678.' });
    return e;
  };

  const submitAdd = async () => {
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error(t('employees:directory.fix_errors', { defaultValue: 'Please fix the highlighted fields.' }));
      return;
    }
    setSaving(true);
    try {
      await createEmployee({
        ...form,
        phone: form.phone?.trim() || undefined,
        middle_name: form.middle_name?.trim() || undefined,
        preferred_name: form.preferred_name?.trim() || undefined,
        // Draft → the backend auto-starts the matching onboarding instance.
        ...(startOnboarding ? { status: 'Draft' } : {}),
      });
      if (startOnboarding) {
        toast.success(t('employees:directory.onboarding_started', {
          defaultValue: 'Onboarding started — open the profile to track it.',
        }), { duration: 6000 });
      } else {
        toast.success(t('employees:directory.employee_added', { name: `${form.first_name} ${form.last_name}` }));
      }
      setForm(emptyForm);
      setErrors({});
      setAddOpen(false);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('employees:directory.add_failed')), { duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  // Debounce the search box so we hit the backend at most once per pause.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  // Server-side search: query the backend by term (scales to large tenants);
  // with no term, load the full directory. Department/status filter client-side.
  useEffect(() => {
    let activeReq = true;
    if (loadedOnce.current) setSearching(true); else setLoading(true);
    (async () => {
      try {
        const [deptRes, emps, outToday, titleRes] = await Promise.all([
          getDepartments().catch(() => ({ items: [] as any[] })),
          debouncedSearch
            ? getEmployees({ search: debouncedSearch, page: 1, page_size: 100 }).then((r) => r.items || [])
            : getAllEmployees(),
          // days_ahead=0 → approved leave that overlaps today (who's out now)
          getTeamOut(0).catch(() => [] as any[]),
          getJobTitles().catch(() => ({ items: [] as any[] })),
        ]);
        if (!activeReq) return;
        const deptMap = new Map<string, string>((deptRes.items || []).map((d: any) => [d.id, d.name]));
        const outSet = new Set<string>((outToday as any[]).map((o) => String(o.employee_id)));
        // Readiness badges load separately below — the heaviest call must not
        // gate first paint of the directory.
        const missingById = readinessMapRef.current;
        const mapped: Employee[] = (emps as ApiEmployee[]).map((e) => {
          // On-leave reflects real, current approved leave; otherwise fall back
          // to the stored employee status (active / inactive / terminated).
          const onLeaveNow = !!e.user_id && outSet.has(String(e.user_id));
          // Contract expired but the employee is still Active → HR should intervene.
          const contractExpired = !!e.contract_end_date
            && new Date(e.contract_end_date) < new Date()
            && mapStatus(e.status) === 'active' && !onLeaveNow;
          return {
            id: e.id,
            userId: e.user_id ? String(e.user_id) : undefined,
            name: `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || e.email,
            position: e.position || '—',
            department: (e.department_id && deptMap.get(e.department_id)) || 'Unassigned',
            email: e.email,
            phone: e.phone || '',
            status: onLeaveNow ? 'on_leave' : mapStatus(e.status),
            avatar: e.profile_picture_url || undefined,
            hireDate: e.hire_date || '',
            managerId: e.manager_id || undefined,
            isDraft: (e.status || '').toLowerCase() === 'draft',
            incompleteCount: missingById.get(e.id),
            contractExpired,
          };
        });
        setEmployees(mapped);
        // Department dropdown = all tenant departments (stable across searches).
        setDepartmentOptions(
          Array.from(new Set((deptRes.items || []).map((d: any) => d.name).filter(Boolean))).sort(),
        );
        setTitleOptions(
          Array.from(new Set(((titleRes as any).items || []).map((tt: any) => tt.name).filter(Boolean))).sort() as string[],
        );
        setError(null);
      } catch (err: any) {
        if (activeReq) setError(err?.response?.data?.detail || err?.message || t('employees:directory.load_failed'));
      } finally {
        if (activeReq) { setLoading(false); setSearching(false); loadedOnce.current = true; }
      }
    })();
    return () => { activeReq = false; };
  }, [debouncedSearch, reloadKey]);

  // Deferred readiness badges: fetch after (in parallel with) the main data
  // and patch the Draft "N to activate" counts in when they arrive.
  const readinessMapRef = useRef(new Map<string, number>());
  useEffect(() => {
    let activeReq = true;
    getReadinessSummary()
      .then((readiness) => {
        if (!activeReq || !readiness) return;
        const map = new Map<string, number>(
          (readiness.least_complete || []).map((r: any) => [r.employee_id, r.missing_count]),
        );
        readinessMapRef.current = map;
        setEmployees((prev) => prev.map((e) => ({ ...e, incompleteCount: map.get(e.id) })));
      })
      .catch(() => { /* badges are best-effort */ });
    return () => { activeReq = false; };
  }, [reloadKey]);

  // Text search is handled server-side; client-side only applies the filters.
  const filteredEmployees = employees.filter((employee) => {
    const matchesDepartment = departmentFilter === 'all' || employee.department === departmentFilter;
    const matchesStatus = statusFilter === 'all'
      || (statusFilter === 'expired_contract' ? !!employee.contractExpired : employee.status === statusFilter);
    return matchesDepartment && matchesStatus;
  });

  // Pin Dana at the top when the active filters/search don't exclude her. She's
  // shown in the grid but NOT added to the employee count (she's a virtual record).
  const showDana =
    (departmentFilter === 'all' || departmentFilter === DANA.department) &&
    (statusFilter === 'all' || statusFilter === 'active') &&
    danaMatchesSearch(debouncedSearch);
  const listed = showDana ? [DANA_ENTRY, ...filteredEmployees] : filteredEmployees;

  const getPageTitle = () => {
    if (isEmployee && !isManager) {
      return t('employees:directory.title_employee');
    }
    return t('employees:directory.title_manager');
  };

  const getPageDescription = () => {
    if (isEmployee && !isManager) {
      return t('employees:directory.description_employee');
    }
    return t('employees:directory.description_manager');
  };

  const fieldCls =
    'mt-1 w-full rounded-[10px] border border-border bg-cream px-3 py-2 text-sm text-foreground outline-none focus:border-orange-400';

  return (
    <div className="space-y-6">
      {addOpen && canWrite && (
        <Card className="border-orange-200 dark:border-orange-500/30">
          <CardHeader className="rounded-t-xl flex-row items-center">
            <CardTitle className="flex-1 text-orange-700">{t('employees:directory.add_employee')}</CardTitle>
            <button type="button" onClick={() => setAddOpen(false)} aria-label={t('employees:directory.close')} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </CardHeader>
          <CardContent className="pt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {([
              ['first_name', t('employees:directory.field_first_name'), 'text'],
              ['last_name', t('employees:directory.field_last_name'), 'text'],
              ['middle_name', t('employees:directory.field_middle_name', { defaultValue: 'Middle name' }), 'text'],
              ['preferred_name', t('employees:directory.field_preferred_name', { defaultValue: 'Preferred name' }), 'text'],
              ['email', t('employees:directory.field_email'), 'email'],
              ['employee_id', t('employees:directory.field_employee_id'), 'text'],
              ['position', t('employees:directory.field_position'), 'text'],
              ['hire_date', t('employees:directory.field_hire_date'), 'date'],
              ['phone', t('employees:directory.field_phone'), 'tel'],
            ] as const).map(([key, label, type]) => (
              <div key={key}>
                <label className="text-sm font-medium text-foreground" htmlFor={`emp-${key}`}>{label}</label>
                {key === 'phone' ? (
                  <PhoneInput
                    id={`emp-${key}`}
                    value={(form.phone as string) || ''}
                    onChange={(v) => { setForm({ ...form, phone: v }); if (errors.phone) setErrors((p) => ({ ...p, phone: undefined })); }}
                  />
                ) : (
                  <input
                    id={`emp-${key}`}
                    type={type}
                    // Job title (position) suggests from the admin-defined catalog
                    // but still accepts a custom value (catalog + free-text).
                    list={key === 'position' ? 'emp-title-options' : undefined}
                    value={(form[key] as string) ?? ''}
                    onChange={(e) => { setForm({ ...form, [key]: e.target.value }); if (errors[key]) setErrors((p) => ({ ...p, [key]: undefined })); }}
                    aria-invalid={!!errors[key]}
                    className={`${fieldCls} ${errors[key] ? 'border-danger focus:border-danger' : ''}`}
                  />
                )}
                {errors[key] && <p className="text-xs text-danger mt-1">{errors[key]}</p>}
              </div>
            ))}
            <datalist id="emp-title-options">
              {titleOptions.map((tt) => <option key={tt} value={tt} />)}
            </datalist>
            <div className="sm:col-span-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 mt-0.5 accent-orange-500"
                  checked={startOnboarding}
                  onChange={(e) => setStartOnboarding(e.target.checked)}
                />
                <span className="text-sm text-foreground">
                  {t('employees:directory.start_onboarding', { defaultValue: 'Start with onboarding (create as Draft)' })}
                  <span className="block text-xs text-muted-foreground">
                    {t('employees:directory.start_onboarding_hint', {
                      defaultValue: 'The new hire stays payroll-blocked until the onboarding checklist completes.',
                    })}
                  </span>
                </span>
              </label>
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <Button onClick={submitAdd} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">
                {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} {t('employees:directory.add_employee')}
              </Button>
              <Button variant="outline" onClick={() => setAddOpen(false)} disabled={saving}>{t('employees:directory.cancel')}</Button>
            </div>
          </CardContent>
        </Card>
      )}
      <Card className="border-2 border-orange-100 dark:border-orange-500/20 hover:border-orange-300 dark:hover:border-orange-500/40 transition-all">
        <CardHeader className="bg-gradient-to-r from-orange-50 dark:from-orange-500/[0.08] to-amber-50 dark:to-amber-500/[0.05] rounded-t-xl">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2 text-orange-700">
                {isEmployee && !isManager ? (
                  <Eye className="w-5 h-5 text-orange-600" />
                ) : (
                  <Users className="w-5 h-5 text-orange-600" />
                )}
                {getPageTitle()} ({filteredEmployees.length} {t('employees:directory.employees_count')})
                {searching && <Loader2 className="w-4 h-4 animate-spin text-orange-400" />}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">{getPageDescription()}</p>
            </div>
            
            {(canWrite || canImport) && (
              <div className="flex gap-2">
                {canWrite && (
                  <Button
                    onClick={() => setAddOpen((v) => !v)}
                    className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white border-0"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    {t('employees:directory.add_employee')}
                  </Button>
                )}
                {canImport && (
                  <Button
                    variant="outline"
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    onClick={() => toast(t('employees:directory.import_coming_soon'))}
                  >
                    {t('employees:directory.import_employees')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <EmployeeSearchFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            departmentFilter={departmentFilter}
            onDepartmentChange={setDepartmentFilter}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            departments={departmentOptions}
          />

          <ViewModeToggle
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />

          {loading ? (
            <div className="flex items-center justify-center py-16 text-orange-600">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span className="text-sm text-muted-foreground">{t('employees:directory.loading')}</span>
            </div>
          ) : error ? (
            <div className="p-6 bg-danger-soft border border-danger rounded-lg text-center">
              <p className="text-sm text-danger-strong mb-3">{error}</p>
              <button
                type="button"
                onClick={() => { setError(null); setReloadKey((k) => k + 1); }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700"
              >
                <Loader2 className={`w-4 h-4 ${searching ? 'animate-spin' : ''}`} />
                {t('employees:directory.retry')}
              </button>
            </div>
          ) : listed.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">
                {debouncedSearch
                  ? t('employees:directory.no_match_search', { term: debouncedSearch })
                  : employees.length === 0
                    ? t('employees:directory.no_employees_yet')
                    : t('employees:directory.no_match_filters')}
              </p>
            </div>
          ) : viewMode === 'cards' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {listed.map((employee) => (
                <EmployeeCard key={employee.id} employee={employee} />
              ))}
            </div>
          ) : (
            <div className="space-y-1">
              {listed.map((employee) => (
                <EmployeeListItem key={employee.id} employee={employee} />
              ))}
            </div>
          )}

          {!canManageEmployees && (
            <div className="mt-6 p-4 bg-info-soft border border-info rounded-lg">
              <div className="text-sm text-info-strong">
                <strong>💡 {t('employees:directory.tip_label')}:</strong> {t('employees:directory.tip')}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
